import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { DownloadIcon, FilterIcon, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const NationalCoverage = () => {
  const [timeframe, setTimeframe] = useState('30days');
  // Sample data for national payers coverage status
  const coverageStatusData = [{
    name: 'Positive Coverage',
    value: 35,
    lives: '33.5M',
    color: '#22c55e'
  }, {
    name: 'Negative Coverage',
    value: 55,
    lives: '52.6M',
    color: '#ef4444'
  }, {
    name: 'No Policy',
    value: 10,
    lives: '9.5M',
    color: '#eab308'
  }];
  // Sample data for national payers
  const nationalPayersData = [{
    name: 'UnitedHealthcare',
    status: 'negative',
    lives: '45.8M',
    percentTotal: 24.1,
    lastUpdate: 'Mar 15, 2025',
    priorAuth: 'Yes',
    policyNumber: 'T-2025-12'
  }, {
    name: 'Anthem',
    status: 'negative',
    lives: '41.2M',
    percentTotal: 21.7,
    lastUpdate: 'Feb 22, 2025',
    priorAuth: 'Yes',
    policyNumber: 'CG-SURG-98'
  }, {
    name: 'Aetna',
    status: 'negative',
    lives: '22.5M',
    percentTotal: 11.8,
    lastUpdate: 'Apr 10, 2025',
    priorAuth: 'Yes',
    policyNumber: '0083'
  }, {
    name: 'Cigna',
    status: 'negative',
    lives: '20.1M',
    percentTotal: 10.6,
    lastUpdate: 'Jan 30, 2025',
    priorAuth: 'Yes',
    policyNumber: '0563'
  }, {
    name: 'Humana',
    status: 'positive',
    lives: '16.8M',
    percentTotal: 8.9,
    lastUpdate: 'Dec 05, 2024',
    priorAuth: 'No',
    policyNumber: 'HCS-0425'
  }, {
    name: 'Kaiser Permanente',
    status: 'no-policy',
    lives: '12.4M',
    percentTotal: 6.5,
    lastUpdate: 'N/A',
    priorAuth: 'N/A',
    policyNumber: 'N/A'
  }, {
    name: 'Centene',
    status: 'negative',
    lives: '11.5M',
    percentTotal: 6.1,
    lastUpdate: 'Mar 01, 2025',
    priorAuth: 'Yes',
    policyNumber: 'CP.MP.168'
  }, {
    name: 'CVS Health',
    status: 'negative',
    lives: '9.8M',
    percentTotal: 5.2,
    lastUpdate: 'Feb 15, 2025',
    priorAuth: 'Yes',
    policyNumber: 'CVS-0921'
  }];
  // Sample data for coverage trends
  const coverageTrendData = [{
    month: 'Jan',
    positive: 28,
    negative: 62,
    noPolicy: 10
  }, {
    month: 'Feb',
    positive: 30,
    negative: 60,
    noPolicy: 10
  }, {
    month: 'Mar',
    positive: 32,
    negative: 58,
    noPolicy: 10
  }, {
    month: 'Apr',
    positive: 33,
    negative: 57,
    noPolicy: 10
  }, {
    month: 'May',
    positive: 35,
    negative: 55,
    noPolicy: 10
  }, {
    month: 'Jun',
    positive: 35,
    negative: 55,
    noPolicy: 10
  }];
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-189">
      <div className="mb-6" data-id="element-190">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-191">
          <div data-id="element-192">
            <div className="flex items-center" data-id="element-193">
              <Link to="/coverage" className="mr-2 text-blue-600 hover:text-blue-800" data-id="element-194">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-195" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-196">
                National Payers Coverage
              </h1>
            </div>
            <p className="mt-1 text-sm text-gray-500" data-id="element-197">
              Coverage status across major national insurance companies
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-198">
            <select className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-199">
              <option data-id="element-200">Freedom® PNS System</option>
              <option data-id="element-201">All Products</option>
            </select>
            <div className="flex p-1 bg-white rounded-lg shadow" data-id="element-202">
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '30days' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('30days')} data-id="element-203">
                30 Days
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '90days' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('90days')} data-id="element-204">
                90 Days
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '12months' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('12months')} data-id="element-205">
                12 Months
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-3" data-id="element-206">
        <div className="bg-white rounded-lg shadow p-6" data-id="element-207">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-208">
            Total Covered Lives
          </h3>
          <div className="mt-2 flex items-baseline" data-id="element-209">
            <p className="text-3xl font-bold text-gray-900" data-id="element-210">95.6M</p>
          </div>
          <p className="mt-1 text-sm text-gray-500" data-id="element-211">
            Across all national payers
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6" data-id="element-212">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-213">
            Positive Coverage
          </h3>
          <div className="mt-2 flex items-baseline" data-id="element-214">
            <p className="text-3xl font-bold text-green-600" data-id="element-215">33.5M</p>
            <p className="ml-2 text-sm font-medium text-green-600" data-id="element-216">35%</p>
          </div>
          <p className="mt-1 text-sm text-gray-500" data-id="element-217">
            Lives with favorable coverage
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6" data-id="element-218">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-219">
            Negative Coverage
          </h3>
          <div className="mt-2 flex items-baseline" data-id="element-220">
            <p className="text-3xl font-bold text-red-600" data-id="element-221">52.6M</p>
            <p className="ml-2 text-sm font-medium text-red-600" data-id="element-222">55%</p>
          </div>
          <p className="mt-1 text-sm text-gray-500" data-id="element-223">
            Lives with unfavorable coverage
          </p>
        </div>
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2" data-id="element-224">
        {/* Coverage Status Chart */}
        <div className="bg-white rounded-lg shadow" data-id="element-225">
          <div className="p-4 border-b border-gray-200" data-id="element-226">
            <div className="flex items-center justify-between" data-id="element-227">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-228">
                Coverage Status Distribution
              </h3>
              <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-229">
                <DownloadIcon className="w-5 h-5" data-id="element-230" />
              </button>
            </div>
          </div>
          <div className="p-4 flex flex-col items-center" data-id="element-231">
            <div className="h-64 w-full" data-id="element-232">
              <ResponsiveContainer width="100%" height="100%" data-id="element-233">
                <PieChart data-id="element-234">
                  <Pie data={coverageStatusData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                  name,
                  percent
                }) => `${name}: ${(percent * 100).toFixed(1)}%`} data-id="element-235">
                    {coverageStatusData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-236" />)}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}% (${props.payload.lives})`, name]} data-id="element-237" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-center justify-center space-x-6" data-id="element-238">
              {coverageStatusData.map((item, index) => <div key={index} className="flex items-center" data-id="element-239">
                  <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: item.color
              }} data-id="element-240"></div>
                  <span className="ml-2 text-sm text-gray-600" data-id="element-241">
                    {item.name}: {item.lives}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
        {/* Coverage Trend Chart */}
        <div className="bg-white rounded-lg shadow" data-id="element-242">
          <div className="p-4 border-b border-gray-200" data-id="element-243">
            <div className="flex items-center justify-between" data-id="element-244">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-245">
                Coverage Trend (Last 6 Months)
              </h3>
              <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-246">
                <DownloadIcon className="w-5 h-5" data-id="element-247" />
              </button>
            </div>
          </div>
          <div className="p-4" data-id="element-248">
            <div className="h-64" data-id="element-249">
              <ResponsiveContainer width="100%" height="100%" data-id="element-250">
                <BarChart data={coverageTrendData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-251">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-252" />
                  <XAxis dataKey="month" data-id="element-253" />
                  <YAxis tickFormatter={value => `${value}%`} data-id="element-254" />
                  <Tooltip formatter={value => [`${value}%`]} data-id="element-255" />
                  <Legend data-id="element-256" />
                  <Bar dataKey="positive" stackId="a" fill="#22c55e" name="Positive" data-id="element-257" />
                  <Bar dataKey="negative" stackId="a" fill="#ef4444" name="Negative" data-id="element-258" />
                  <Bar dataKey="noPolicy" stackId="a" fill="#eab308" name="No Policy" data-id="element-259" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      {/* National Payers Table */}
      <div className="bg-white rounded-lg shadow mb-8" data-id="element-260">
        <div className="p-4 border-b border-gray-200" data-id="element-261">
          <div className="flex items-center justify-between" data-id="element-262">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-263">
              National Payers Coverage Details
            </h3>
            <div className="flex items-center space-x-2" data-id="element-264">
              <button className="p-2 text-gray-500 hover:text-gray-700" data-id="element-265">
                <FilterIcon className="w-5 h-5" data-id="element-266" />
              </button>
              <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-267">
                <DownloadIcon className="w-5 h-5" data-id="element-268" />
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-269">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-270">
            <thead className="bg-gray-50" data-id="element-271">
              <tr data-id="element-272">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-273">
                  Payer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-274">
                  Coverage Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-275">
                  Covered Lives
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-276">
                  % of Total
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-277">
                  Prior Auth
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-278">
                  Policy Number
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-279">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-280">
              {nationalPayersData.map((payer, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-281">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-282">
                    <div className="text-sm font-medium text-blue-600" data-id="element-283">
                      {payer.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-284">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${payer.status === 'positive' ? 'bg-green-100 text-green-800' : payer.status === 'negative' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-285">
                      {payer.status === 'positive' ? 'Positive' : payer.status === 'negative' ? 'Negative' : 'No Policy'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-286">
                    <div className="text-sm text-gray-900" data-id="element-287">{payer.lives}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-288">
                    <div className="text-sm text-gray-900" data-id="element-289">
                      {payer.percentTotal}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-290">
                    <div className="text-sm text-gray-500" data-id="element-291">
                      {payer.priorAuth}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-292">
                    <div className="text-sm text-gray-500" data-id="element-293">
                      {payer.policyNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-294">
                    <div className="text-sm text-gray-500" data-id="element-295">
                      {payer.lastUpdate}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-200" data-id="element-296">
          <div className="flex justify-between items-center" data-id="element-297">
            <div className="text-sm text-gray-500" data-id="element-298">
              Showing 8 of 12 national payers
            </div>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-299">
              View All National Payers →
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default NationalCoverage;